import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { ApiError } from "@/api/client";
import { opsSupplierApi, type OpsSupplier } from "@/api/opsSupplierClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/framework-originated/i18n";

export function SupplierAccountPanel({
  supplier,
  onChanged,
}: {
  supplier: OpsSupplier;
  onChanged: (message: string) => Promise<void>;
}) {
  const { t } = useTranslation("opsSuppliers");
  const [contactName, setContactName] = useState(supplier.contact_name);
  const [contactEmail, setContactEmail] = useState(supplier.contact_email);
  const [limit, setLimit] = useState(String(supplier.distribute_limit));
  const [prefix, setPrefix] = useState(supplier.code_prefix);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setContactName(supplier.contact_name);
    setContactEmail(supplier.contact_email);
    setLimit(String(supplier.distribute_limit));
    setPrefix(supplier.code_prefix);
  }, [supplier]);

  const profileMut = useMutation({
    mutationFn: () => opsSupplierApi.update(supplier.id, {
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
      distribute_limit: Number(limit),
      code_prefix: prefix.trim(),
    }),
    onSuccess: () => {
      setError(null);
      void onChanged(t("updated"));
    },
    onError: (value) => setError(value instanceof ApiError ? value.message : String(value)),
  });

  const resetMut = useMutation({
    mutationFn: () => opsSupplierApi.update(supplier.id, { password }),
    onSuccess: () => {
      setPassword("");
      setPasswordConfirm("");
      setError(null);
      void onChanged(t("detail.passwordResetSuccess"));
    },
    onError: (value) => setError(value instanceof ApiError ? value.message : String(value)),
  });

  const statusMut = useMutation({
    mutationFn: async () => {
      if (supplier.enabled) await opsSupplierApi.disable(supplier.id);
      else await opsSupplierApi.update(supplier.id, { enabled: true });
    },
    onSuccess: () => {
      setError(null);
      void onChanged(supplier.enabled ? t("disabled") : t("detail.restored"));
    },
    onError: (value) => setError(value instanceof ApiError ? value.message : String(value)),
  });

  const passwordValid = password.length >= 8 && password === passwordConfirm;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {error && <div className="lg:col-span-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div>}
      <Card>
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); profileMut.mutate(); }}>
            <div className="space-y-1">
              <Label>{t("username")}</Label>
              <Input value={supplier.username} readOnly disabled />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t("contactName")}</Label>
                <Input value={contactName} maxLength={100} onChange={(event) => setContactName(event.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>{t("contactEmail")}</Label>
                <Input type="email" value={contactEmail} maxLength={255} onChange={(event) => setContactEmail(event.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>{t("distributeLimit")}</Label>
                <Input type="number" min="0" value={limit} onChange={(event) => setLimit(event.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label>{t("codePrefix")}</Label>
                <Input value={prefix} maxLength={8} pattern="[A-Za-z0-9]{1,8}" onChange={(event) => setPrefix(event.target.value)} required />
              </div>
            </div>
            <div className="flex justify-between gap-2">
              <Button
                type="button"
                variant={supplier.enabled ? "destructive" : "secondary"}
                disabled={statusMut.isPending}
                onClick={() => {
                  if (supplier.enabled && !confirm(t("disableConfirm", { name: supplier.username }))) return;
                  statusMut.mutate();
                }}
              >
                {supplier.enabled ? t("disable") : t("detail.restore")}
              </Button>
              <Button type="submit" disabled={profileMut.isPending}>{t("save")}</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); if (passwordValid) resetMut.mutate(); }}>
            <div>
              <h2 className="text-sm font-semibold">{t("detail.resetPassword")}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{t("detail.resetPasswordHint")}</p>
            </div>
            <div className="space-y-1">
              <Label>{t("detail.newPassword")}</Label>
              <Input type="password" autoComplete="new-password" minLength={8} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label>{t("detail.confirmPassword")}</Label>
              <Input type="password" autoComplete="new-password" minLength={8} maxLength={128} value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} required />
            </div>
            <Button type="submit" disabled={!passwordValid || resetMut.isPending}>{t("detail.resetPassword")}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function SupplierKeyPanel({ supplier }: { supplier: OpsSupplier }) {
  const { t } = useTranslation("opsSuppliers");
  const [name, setName] = useState("");
  const [plainKey, setPlainKey] = useState("");
  const [supplierField, setSupplierField] = useState("");
  const [baseURL, setBaseURL] = useState("");
  const [models, setModels] = useState("");
  const [protocolType, setProtocolType] = useState("");
  const [remark, setRemark] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => opsSupplierApi.createKey(supplier.id, {
      name: name.trim(),
      key: plainKey,
      supplier_field: supplierField.trim() || undefined,
      base_url: baseURL.trim() || undefined,
      models: models.trim() || undefined,
      protocol_type: protocolType ? Number(protocolType) : undefined,
      remark: remark.trim() || undefined,
    }),
    onSuccess: (created) => {
      setPlainKey("");
      setName("");
      setError(null);
      setMessage(created.suspected_duplicate
        ? t("detail.keyDuplicate", { id: created.id, duplicateId: created.duplicate_of_id })
        : t("detail.keyCreated", { id: created.id, hint: created.key_hint }));
    },
    onError: (value) => {
      setPlainKey("");
      setMessage(null);
      setError(value instanceof ApiError ? value.message : String(value));
    },
  });

  return (
    <Card>
      <CardContent className="pt-6">
        {(message || error) && (
          <div className={`mb-4 rounded-md border px-3 py-2 text-sm ${error ? "border-destructive/40 text-destructive" : "border-amber-300 bg-amber-50"}`}>
            {error ?? message}
          </div>
        )}
        <form className="grid grid-cols-2 gap-3" onSubmit={(event) => { event.preventDefault(); createMut.mutate(); }}>
          <div className="col-span-2 flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            <div>
              <h2 className="text-sm font-semibold">{t("detail.createKeyForSupplier")}</h2>
              <p className="text-xs text-muted-foreground">{t("detail.createKeyHint", { username: supplier.username })}</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t("detail.keyName")}</Label>
            <Input value={name} maxLength={100} onChange={(event) => setName(event.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label>{t("detail.keyPlain")}</Label>
            <Input type="password" autoComplete="off" value={plainKey} onChange={(event) => setPlainKey(event.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label>{t("detail.supplierField")}</Label>
            <Input value={supplierField} maxLength={100} onChange={(event) => setSupplierField(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("detail.baseURL")}</Label>
            <Input value={baseURL} maxLength={255} onChange={(event) => setBaseURL(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("detail.models")}</Label>
            <Input value={models} maxLength={500} onChange={(event) => setModels(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("detail.protocolType")}</Label>
            <Input type="number" min="0" value={protocolType} onChange={(event) => setProtocolType(event.target.value)} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t("detail.remark")}</Label>
            <Input value={remark} maxLength={2000} onChange={(event) => setRemark(event.target.value)} />
          </div>
          <div className="col-span-2 flex justify-end">
            <Button type="submit" disabled={createMut.isPending || !plainKey || !name.trim()}>{t("detail.createKey")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
